import React from "react";

// 20261021 Workplace/Task deep search highlight helpers.
// Keyword words are split on the same delimiters as the backend's Utils.GetWhere ("+-;,| "),
// so what gets highlighted is exactly what the server matched on.

const HIGHLIGHT_NAME = "wp-search";

export const splitSearchWords = (keyword: string | null | undefined): string[] =>
  Array.from(new Set((keyword ?? "").split(/[+\-;,| ]+/).map(w => w.trim()).filter(w => w !== "")));

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** plain text with every keyword word wrapped in <mark> (case-insensitive) */
export const MarkedText = ({ text, words }: { text: string; words: string[] }) => {
  if (!text || words.length === 0) return <>{text}</>;
  const re = new RegExp("(" + words.map(escapeRegExp).join("|") + ")", "gi");
  const lower = words.map(w => w.toLowerCase());
  return (
    <>
      {text.split(re).map((part, i) =>
        lower.includes(part.toLowerCase()) ? <mark key={i} className="wp-search-mark">{part}</mark> : <React.Fragment key={i}>{part}</React.Fragment>
      )}
    </>
  );
};

/**
 * Paints every keyword occurrence inside `container` with the CSS Custom Highlight API (::highlight(wp-search)).
 * It doesn't touch the DOM, so it can't fight React re-renders or the quill/innerHTML message bodies.
 * Browsers without the API (older iOS < 17.2) just get no highlight.
 */
export const applyDomSearchHighlight = (container: HTMLElement | null, words: string[]) => {
  const css: any = (window as any).CSS;
  const HighlightCtor: any = (window as any).Highlight;
  if (!css?.highlights || !HighlightCtor) return;
  css.highlights.delete(HIGHLIGHT_NAME);
  if (!container || words.length === 0) return;

  const lower = words.map(w => w.toLowerCase());
  const ranges: Range[] = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = (node.nodeValue ?? "").toLowerCase();
    if (!text.trim()) continue;
    for (const w of lower) {
      for (let idx = text.indexOf(w); idx >= 0; idx = text.indexOf(w, idx + w.length)) {
        const r = document.createRange();
        r.setStart(node, idx);
        r.setEnd(node, idx + w.length);
        ranges.push(r);
      }
    }
  }
  if (ranges.length > 0) css.highlights.set(HIGHLIGHT_NAME, new HighlightCtor(...ranges));
};

export const clearDomSearchHighlight = () => {
  const css: any = (window as any).CSS;
  css?.highlights?.delete(HIGHLIGHT_NAME);
};
